import React from 'react';

import classNames from 'classnames';
import { POSITION_TYPE_COLOURS, POSITION_TYPE_ORDER, positionTypeLabel } from 'Components/people/positionTypes';

/**
 * The People tab's position-type filter.
 *
 * Structure and the last-ticked-checkbox rule are taken from
 * `Components/workforce/WorkforceTypeFilterPanel`, but the swatches are solid
 * fills rather than that panel's outlined node replicas, because here they are a
 * legend for the headcount ramp and the composition split — the reader has to
 * match a swatch to a chart series, so it must be the same solid colour.
 *
 * The last ticked type is disabled rather than merely ignored: an empty filter
 * would blank the dashboard, the positions table and the org chart at once, and
 * the preference endpoint rejects an empty `position_types` with a 422.
 *
 * @param {string[]} value     - selected type codes, e.g. ['P', 'S']
 * @param {Function} onChange  - called with the new array of type codes
 */
export default function PeoplePositionTypeFilter({ value = [], onChange }) {
    const selected = POSITION_TYPE_ORDER.filter((type) => value.includes(type));
    const isLastTicked = selected.length === 1;

    function toggle(type) {
        onChange(
            selected.includes(type)
                ? selected.filter((selectedType) => selectedType !== type)
                : POSITION_TYPE_ORDER.filter((option) => option === type || selected.includes(option)),
        );
    }

    return (
        <div className="tw-flex tw-flex-row tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2" role="group" aria-label={$t('Position types')}>
            {POSITION_TYPE_ORDER.map((type) => {
                const checked = selected.includes(type);
                const disabled = checked && isLastTicked;

                return (
                    <label
                        key={type}
                        title={disabled ? $t('At least one position type must stay selected') : undefined}
                        className={classNames(
                            'tw-flex tw-items-center tw-gap-x-1.5 tw-mb-0 tw-select-none',
                            disabled ? 'tw-cursor-not-allowed tw-opacity-60' : 'tw-cursor-pointer',
                        )}
                    >
                        <input type="checkbox" className="input-checkbox" checked={checked} disabled={disabled} onChange={() => !disabled && toggle(type)} />
                        <span
                            aria-hidden="true"
                            className="tw-inline-block tw-w-3 tw-h-3 tw-rounded-sm tw-shrink-0"
                            style={{ background: POSITION_TYPE_COLOURS[type] }}
                        />
                        <span className="tw-text-sm tw-text-charcoal-75">{positionTypeLabel(type)}</span>
                    </label>
                );
            })}
        </div>
    );
}
